import type { AlertRule, Integration } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { AlertRuleDialog } from '@/components/alert-rule-dialog';
import { EmptyState, ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import {
  describeAction,
  describeCondition,
  describeFilter,
  minutes,
  toInput,
} from '@/lib/alert-rules';
import { useAuth } from '@/lib/auth';
import { allows, useOrgRole } from '@/lib/roles';

/** Which rule the editor is open for: an existing one, or a rule to create. */
type Editing = { rule?: AlertRule } | null;

export function AlertsPage() {
  const { projectId = '' } = useParams();
  const { client } = useAuth();
  const [editing, setEditing] = useState<Editing>(null);

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => client.getProject(projectId),
  });
  const orgId = project.data?.org_id;
  const role = useOrgRole(orgId);
  const canManage = allows(role, 'admin');

  const rules = useQuery({
    queryKey: ['alert-rules', projectId],
    queryFn: () => client.listAlertRules(projectId),
  });

  // Actions name an integration rather than carrying its URL, so the list needs
  // them to say "Slack: #alerts" instead of a UUID.
  const integrations = useQuery({
    queryKey: ['integrations', orgId],
    queryFn: () => client.listIntegrations(orgId ?? ''),
    enabled: Boolean(orgId),
  });

  if (rules.isLoading) return <ListSkeleton rows={3} />;
  if (rules.isError) return <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />;

  const newRule = (
    <Button onClick={() => setEditing({})}>
      <Plus />
      New rule
    </Button>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Alert rules</h1>
          <p className="text-sm text-muted-foreground">
            Rules fire on change — a new issue, a regression, a spike — never on steady state.
          </p>
        </div>
        {canManage && rules.data && rules.data.length > 0 && newRule}
      </div>

      {rules.data?.length === 0 ? (
        <EmptyState
          icon={<Bell className="size-5" />}
          title="No alert rules"
          description="Create a rule to be notified when something new breaks. Until then, issues are recorded but nobody is paged."
          action={canManage ? newRule : undefined}
        />
      ) : (
        <div className="space-y-3">
          {rules.data?.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              projectId={projectId}
              integrations={integrations.data}
              canManage={canManage}
              onEdit={() => setEditing({ rule })}
            />
          ))}
        </div>
      )}

      {editing && (
        <AlertRuleDialog
          // Remounting per rule is what resets the draft, so the key has to
          // change with the rule being edited.
          key={editing.rule?.id ?? 'new'}
          projectId={projectId}
          orgId={orgId}
          rule={editing.rule}
          integrations={integrations.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function RuleCard({
  rule,
  projectId,
  integrations,
  canManage,
  onEdit,
}: {
  rule: AlertRule;
  projectId: string;
  /** Undefined until the organization's integrations have loaded. */
  integrations: Integration[] | undefined;
  canManage: boolean;
  onEdit: () => void;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['alert-rules', projectId] });

  // Disabling replaces the whole rule, which is also why it sends the rule back
  // unchanged apart from the flag.
  const setActive = useMutation({
    mutationFn: (is_active: boolean) =>
      client.updateAlertRule(projectId, rule.id, { ...toInput(rule), is_active }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: () => client.deleteAlertRule(projectId, rule.id),
    onSuccess: invalidate,
  });

  const error = setActive.error ?? remove.error;

  return (
    <Card className={rule.is_active ? undefined : 'opacity-70'}>
      <CardHeader className="pb-2">
        <div className="flex items-start gap-2">
          {/* The frequency sits beside the name where it fits and under it where
              it does not, so the menu always keeps its corner. */}
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <CardTitle className="text-base">{rule.name}</CardTitle>
            {!canManage && !rule.is_active && <Badge variant="muted">disabled</Badge>}
            <Badge variant="outline" className="whitespace-nowrap sm:ml-auto">
              at most every {minutes(rule.frequency_minutes)}
            </Badge>
          </div>
          {canManage && (
            <Switch
              className="mt-1"
              checked={setActive.isPending ? setActive.variables : rule.is_active}
              onCheckedChange={(checked) => setActive.mutate(checked)}
              disabled={setActive.isPending}
              aria-label={`${rule.name} enabled`}
              title={rule.is_active ? 'Disable this rule' : 'Enable this rule'}
            />
          )}
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  title={`Manage ${rule.name}`}
                >
                  <MoreHorizontal />
                  <span className="sr-only">Manage {rule.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={onEdit}>
                  <Pencil />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    if (window.confirm(`Delete “${rule.name}”? This cannot be undone.`)) {
                      remove.mutate();
                    }
                  }}
                >
                  <Trash2 />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <CardDescription>
          Fires when {rule.action_match === 'all' ? 'all' : 'any'} of:
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
          {rule.conditions.map((condition, index) => (
            <li key={index}>{describeCondition(condition)}</li>
          ))}
        </ul>

        {rule.filters.length > 0 && (
          <>
            <p className="mt-3 text-sm text-muted-foreground">…but only while:</p>
            <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
              {rule.filters.map((filter, index) => (
                <li key={index}>{describeFilter(filter)}</li>
              ))}
            </ul>
          </>
        )}

        <div className="mt-3 flex flex-wrap gap-1.5">
          {rule.actions.map((action, index) => (
            <Badge key={index} variant="secondary">
              {describeAction(action, integrations)}
            </Badge>
          ))}
        </div>

        {error && <p className="mt-3 text-sm text-destructive">{(error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}
